/**
 * @file Disclosure.jsx
 * @description Small <details>-based disclosure pieces for the public pages:
 *   - splitForDetails: split long copy at a sentence boundary into a short visible head and the rest.
 *   - MoreDetails: shows the head (about 3 lines in a narrow card) and puts the rest in a collapsed
 *     "More details" <details>. The rest stays in the HTML, so crawlers and prerender still see all the SEO text.
 *   - FaqDetails: one FAQ row (white card, bold question, "+" that turns to "x") built on <details>, so the answer
 *     is always in the HTML. Same look as the /aba-agencies FAQ accordion (which keeps its own button version).
 *   Inline styles only. No browser-default triangles: each <summary> is display:flex / inline-flex with list-style none,
 *   which removes the marker in Chrome, Firefox and Safari without a stylesheet.
 * @module components/Disclosure
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";

const NAVY = "#0F1024";
const INDIGO = "#4F46E5";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";
const LIGHT = "#94A3B8";

/**
 * Split `text` at a sentence boundary: `head` is as many whole sentences as fit in `max` characters
 * (always at least the first sentence), `tail` is the rest. No lookbehind (older Safari).
 */
// Words that end with a period but don't end a sentence (place names and the Spanish "EE. UU.").
const ABBREV_END = /(?:^|\s)(?:St|Ste|Ft|Mt|Dr|Jr|Sr|EE|UU|Mr|Mrs|Ms|vs|No)\.\s*$/;

export function splitForDetails(text, max = 150) {
  const str = String(text == null ? "" : text);
  if (str.length <= max) return { head: str, tail: "" };
  const raw = str.match(/[^.!?]+(?:[.!?]+["')\]]*|$)\s*/g) || [str];
  // A period after an abbreviation ("St. Louis", "Ft. Myers", "EE. UU.") is not a sentence end, and neither is a
  // decimal point ("31.0 minutes", "code 4999.200", "47.5%"): glue it to what follows.
  const sentences = [];
  for (const part of raw) {
    const prev = sentences[sentences.length - 1];
    const decimal = prev !== undefined && /\d\.$/.test(prev) && /^\d/.test(part);
    if (prev !== undefined && (decimal || ABBREV_END.test(prev))) sentences[sentences.length - 1] = prev + part;
    else sentences.push(part);
  }
  let head = "";
  for (const s of sentences) {
    if (head && (head + s).length > max) break;
    head += s;
  }
  head = head.trim();
  const tail = str.slice(head.length).trim();
  return { head, tail };
}

const summaryStyle = { display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", color: INDIGO, fontSize: 13, fontWeight: 700, fontFamily: "inherit", listStyle: "none", padding: "4px 0" };

/**
 * @param {object} props
 * @param {string} props.text Full copy; only the first ~`max` characters (whole sentences) are shown up front.
 * @param {number} [props.max]
 * @param {import("react").ReactNode} [props.extra] More content that always lives inside the collapsed part.
 * @param {object} [props.style] Style for the visible paragraph (font size, colour, line height).
 */
export function MoreDetails({ text, max = 150, extra = null, style }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { head, tail } = splitForDetails(text, max);
  const hasMore = Boolean(tail) || Boolean(extra);
  const p = { margin: 0, ...style };
  return (
    <div data-testid="more-details-block">
      {head && <p style={p}>{head}</p>}
      {hasMore && (
        <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)} data-testid="more-details" style={{ marginTop: 6 }}>
          <summary style={summaryStyle}>
            {t("common.moreDetails")}
            <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1, transform: open ? "rotate(45deg)" : "none", transition: "transform 0.18s ease" }}>+</span>
          </summary>
          {tail && <p style={{ ...p, marginTop: 6 }}>{tail}</p>}
          {extra}
        </details>
      )}
    </div>
  );
}

/** One FAQ row on <details>: the answer is always in the HTML (SEO) and the "+" matches the /aba-agencies FAQ. */
export function FaqDetails({ q, a, defaultOpen = false, isMobile }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)} data-testid="faq-item" style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, marginBottom: 10, overflow: "hidden" }}>
      <summary style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, cursor: "pointer", listStyle: "none", padding: isMobile ? "16px 16px" : "17px 22px", fontFamily: T.fontBody }}>
        <span data-testid="faq-question" style={{ fontSize: 14.5, fontWeight: 700, color: NAVY }}>{q}</span>
        <span data-testid="faq-plus" aria-hidden="true" style={{ flex: "none", fontSize: 22, lineHeight: 1, color: LIGHT, transform: open ? "rotate(45deg)" : "none", transition: "transform 0.18s ease" }}>+</span>
      </summary>
      <div data-testid="faq-answer" style={{ padding: isMobile ? "0 16px 16px" : "0 22px 18px", fontSize: 13.5, color: TEXT_MID, lineHeight: 1.7 }}>{a}</div>
    </details>
  );
}
